/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { OptionsObject, SnackbarKey } from 'notistack';
import { forwardRef, memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { INotification } from '../../store/notifications/types';
import { closeSnackbar } from '../../store/notifier/actions';
import { AppEvents } from '../../types/common/AppEvents';
import { useRenderNotificationItem } from '../../utils/useRenderNotificationItem';
import { ErrorIcon } from '../svg-icons/ErrorIcon';
import { InfoIcon } from '../svg-icons/InfoIcon';
import { SuccessIcon } from '../svg-icons/SuccessIcon';
import { WarningIcon } from '../svg-icons/WarningIcon';
import { useSnackbarNotificationStyles } from './SnackbarNotification.styles';

type SnackbarNotificationProps = Pick<OptionsObject, 'variant'> & {
  item: INotification<AppEvents>;
  snackbarKey: SnackbarKey;
};

export const SnackbarNotification = memo(
  forwardRef<HTMLDivElement, SnackbarNotificationProps>(({ item, snackbarKey, variant = 'default' }, ref) => {
    const classes = useSnackbarNotificationStyles();
    const dispatch = useDispatch();
    const renderNotificationItem = useRenderNotificationItem();

    const onRemoveBtnClick = useCallback(() => {
      dispatch(closeSnackbar(snackbarKey));
    }, [dispatch, snackbarKey]);

    const element = renderNotificationItem({
      asSnackbar: true,
      onRemoveBtnClick,
      item,
      onFirstButtonClick: onRemoveBtnClick,
      onSecondButtonClick: onRemoveBtnClick,
      onRootClick: onRemoveBtnClick,
    });

    useEffect(() => {
      if (element === undefined) {
        onRemoveBtnClick();
      }
    }, [element, onRemoveBtnClick]);

    return element ? (
      <div className={classes.root} ref={ref}>
        {!variant || variant === 'default' ? (
          <div className={classes.noVariantOffset} />
        ) : (
          <div className={classes.icon}>
            {variant === 'success' && <SuccessIcon className={classes.successIcon} />}
            {variant === 'error' && <ErrorIcon className={classes.errorIcon} />}
            {variant === 'warning' && <WarningIcon className={classes.warningIcon} />}
            {variant === 'info' && <InfoIcon className={classes.infoIcon} />}
          </div>
        )}
        {element}
      </div>
    ) : (
      <div ref={ref} />
    );
  }),
);
